import { Fragment, useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RepositoryContext } from '../../store/RepositoryProvider';
import API from '../../server/api';
import NavBar from '../../components/NavBar';
import Badge, { BADGE_TYPE } from '../../components/Badge';
import Table, { ALIGN_TABLE } from '../../components/Table';
import DateFormat from '../../components/DateFormat';
import Modal from '../../components/Modal';
import Place from './place';
import Button, { BUTTON_POSITION, BUTTON_TYPE } from '../../components/Button';
import BackBar from '../../components/BackBar';
import log from '../../utils/logger';
import { ScheduleStatus } from '../../models/types';
import Dropdown from '../../components/Dropdown';
import DateTZ from '../../models/date/DateTZ';
import { WebSocketContext, WebSocketProvider } from '../../store/WebSocketProvider';



export const PLACE_READY = {
  [true]: BADGE_TYPE.SUCCESS,
  [false]: BADGE_TYPE.WARN
};

const MyRoutesContent = () => {
  const {t} = useTranslation(['common', 'route']);

  const repository = useContext(RepositoryContext);
  const topic = useContext(WebSocketContext);

  const [schedules, setSchedules] = useState([]);
  const [collapse, setCollapse] = useState(-1);
  const [place, setPlace] = useState(null);
  const [schedule, setSchedule] = useState([]);

  const [showPlaceModal, setShowPlaceModal] = useState(false);

  const toggleCollapse = useCallback((key) => {
    setCollapse(collapse === key ? -1 : key);
  }, [collapse]);

  const handleClosePlaceModal = useCallback(() => {
    setShowPlaceModal(false);
  }, []);

  const handleSelectPlace = (place, schedule) => {
    setPlace(place);
    setSchedule(schedule);
    setShowPlaceModal(true);
  };

  const loadSchedules = () => {
    return repository.list(API.guide.schedules).then((schedules) => {
      const now = new DateTZ().dayjs;
      let _schedules = schedules.filter((sch) => sch.status !== ScheduleStatus.FINISHED && (new DateTZ(sch.date).dayjs.add(sch.duration, 'second').add(4, 'hour').isAfter(now)));
      _schedules.forEach((sch) => {
        sch.date = new DateTZ(sch.date);
        sch.passengers = sch.places.reduce((acm, { passengers: { actual, expected, noSeat } }) => { return { actual: acm.actual + actual, expected: acm.expected + expected, noSeat: acm.noSeat + noSeat } }, { actual: 0, expected: 0, noSeat: 0 });
      });

      _schedules = _schedules.sort((a, b) => {
        const aDate = new DateTZ(a.date).dayjs;
        const bDate = new DateTZ(b.date).dayjs;
        return aDate.isBefore(bDate) ? 1 : bDate.isBefore(aDate) ? -1 : 0;
      });

      setSchedules(_schedules);

      return _schedules;
    }).catch((error) => {
      log.error(error);
      setSchedules([]);

      return [];
    });
  };

  const openPlace = () => {
    if (place) {
      setShowPlaceModal(true);
    }
  };

  const refreshPlace = (placeId, scheduleId) => {
    loadSchedules().then((schedules) => {
      if (placeId) {
        schedules.forEach((sch) => {
          if (sch.id === scheduleId) {
            sch.places.forEach((p) => {
              if (p.id === placeId) {
                setPlace(p);
                setSchedule(sch);
              }
            });
          }
        });
      }
    });
  };

  useEffect(() => {
    loadSchedules().then((schedules) => {
      const now = new DateTZ().dayjs;
      schedules.forEach((sch) => {
        const scheduleDate = new DateTZ(sch.date).dayjs;

        if (now.isAfter(scheduleDate) && now.isBefore(scheduleDate.add(sch.duration, 'second'))) {
          let activePlace = null;

          sch.places.forEach((p) => {
            const placeDate = new DateTZ(p.time).dayjs;

            if (now.isAfter(placeDate) || now.isSame(placeDate)) {
              activePlace = p;
            }
          });

          if (activePlace) {
            setSchedule(sch);
            setPlace(activePlace);
          }
        }
      });
    });
  }, []);

  return (
    <div className="trip-app">
      <NavBar title={(
        <Table>
          <Table.Row>
            <Table.Cell>{t('route:my_routes.title')}</Table.Cell>
            <Table.Cell><span className={place ? '' : 'title-disabled'} style={{paddingLeft: '20px'}} onClick={openPlace}>{t('route:my_routes.boarding')}</span></Table.Cell>
          </Table.Row>
        </Table>
      )} />
      <div className="trip-app-body">
        <Table cols={5}>
          {(schedules.length === 0) && (
            <p>
              {t('common:labels.no_records')}
            </p>
          )}
          {schedules.map((s) => (
            <Fragment key={s.number}>
              <Table.Row onClick={() => toggleCollapse(s.number)}>
                <Table.Cell colspan={2}>
                  {t('route:my_routes.route', { route: s.number })}
                </Table.Cell>
                <Table.Cell colspan={3} align={ALIGN_TABLE.RIGHT}>
                  <DateFormat.DateTime date={s.date} />
                </Table.Cell>
              </Table.Row>
              <Table.Row onClick={() => toggleCollapse(s.number)}>
                <Table.Cell colspan={5} className="small">
                  {s.excursions.map((excursion) => (
                    <div key={excursion.id}>{excursion.title}</div>
                  ))}
                </Table.Cell>
              </Table.Row>
              <Table.Row onClick={() => toggleCollapse(s.number)}>
                <Table.Cell colspan={3} className="small">
                  {s.transports.map((transport) => (
                    <div key={transport.id}>{transport.number}: {transport.title}</div>
                  ))}
                </Table.Cell>
                <Table.Cell colspan={2} className="small" align={ALIGN_TABLE.RIGHT}>
                  <Badge type={PLACE_READY[s.passengers.actual === s.passengers.expected]}>
                    {t('route:my_routes.seat', s.passengers)}
                  </Badge>
                </Table.Cell>
              </Table.Row>
              {s.places.map((place) => (
                <Table.Row key={place.id} className="sub-row" style={collapse === s.number ? {} : { display: 'none' }} onClick={() => handleSelectPlace(place, s)}>
                  <Table.Cell className="small">
                    <div>{!s.date.isSameDate(place.time) && <DateFormat.Day date={place.time ? place.time : s.date} />}</div>
                    <div><DateFormat.Time date={place.time ? place.time : s.date} /></div>
                  </Table.Cell>
                  <Table.Cell className="small" colspan={3}>{place.title}</Table.Cell>
                  <Table.Cell className="small" align={ALIGN_TABLE.RIGHT}>
                    <Badge type={PLACE_READY[place.passengers.actual === place.passengers.expected]}>
                      {t('route:my_routes.count_place', place.passengers)}
                    </Badge>
                  </Table.Cell>
                </Table.Row>
              ))}
              <Table.Row delim={true} />
            </Fragment>
          ))}
        </Table>

        <Modal show={showPlaceModal} onHide={handleClosePlaceModal}>
          <Modal.Header>
            <BackBar title={
              <Table cols={2}>
                <Table.Row>
                  <Table.Cell>{t('route:place.title')}</Table.Cell>
                  <Table.Cell align={ALIGN_TABLE.RIGHT}>
                    {place && (
                      <Badge type={BADGE_TYPE.INFO}>
                        {t('route:my_routes.seat', schedule.passengers)}
                      </Badge>
                    )}
                  </Table.Cell>
                </Table.Row>
              </Table>
            } onBack={handleClosePlaceModal}/>
          </Modal.Header>
          <Modal.Body>
            {showPlaceModal && (
              <>
                {place && (
                  <Table cols={2}>
                    <Table.Row>
                      <Table.Cell>
                        <Dropdown title={<><DateFormat.Time date={place.time ? place.time : schedule.date} /> {place.title}</>}>
                          {schedule && schedule.places.map((p) => (
                            <Dropdown.Item key={p.id} className={place.id === p.id ? 'selected' : ''} onClick={() => setPlace(p)}>
                              <DateFormat.Time date={p.time ? p.time : schedule.date} /> {p.title}
                            </Dropdown.Item>
                          ))}
                        </Dropdown>
                      </Table.Cell>
                      <Table.Cell align={ALIGN_TABLE.RIGHT}>
                        <Badge type={PLACE_READY[schedule.passengers.actual === schedule.passengers.expected]}>
                          {place.passengers.actual}/{place.passengers.expected}
                        </Badge>
                      </Table.Cell>
                    </Table.Row>
                  </Table>
                )}

                <Button.Panel>
                  <Button variant={BUTTON_TYPE.SECONDARY} position={BUTTON_POSITION.NONE} onClick={() => {setPlace(schedule.places[Math.min(schedule.places.findIndex((p) => p.id === place.id) + 1, schedule.places.length - 1)])}}>{t('route:button.next_stop')}</Button>
                </Button.Panel>

                <Place place={place} schedule={schedule} refresh={refreshPlace}/>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={handleClosePlaceModal}>{t('common:button.close')}</Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

const MyRoutes = () => {
  return (
    <WebSocketProvider>
      <MyRoutesContent />
    </WebSocketProvider>
  );
};



export default MyRoutes;